import type { Reducer } from 'redux';

import { mergeFetchedEntities } from '../utils';

import type { State } from './jobsTypes';
import { ActionType } from './jobsTypes';

export const initialState: State = {
  isEditingJob: false,
  editJobSuccess: undefined,
  editJobErrorMessage: undefined,

  isUpdatingJob: false,

  isGettingJobValidationResult: false,

  jobs: {},
  jobAttachments: {},

  isGettingJobsForDeal: false,

  jobUpdateResults: {},

  isSendingInvoiceToCustomer: false,

  isAccpetingQuote: false,
  isDecliningJob: false,
  isConvertingJobToInvoice: false,
  isDuplicatingJob: false,

  isCreatingJob: false,
};

const jobsReducer: Reducer<State> = (state = initialState, action) => {
  switch (action.type) {
    case ActionType.REQUEST_GET_JOBS_FOR_DEAL:
      return {
        ...state,
        isGettingJobsForDeal: true,
      };
    case ActionType.GET_JOBS_FOR_DEAL_SUCCESS:
      return {
        ...state,
        jobs: mergeFetchedEntities({
          cache: state.jobs,
          values: action.payload,
        }),
        isGettingJobsForDeal: false,
      };
    case ActionType.GET_JOBS_FOR_DEAL_FAILURE:
      return {
        ...state,
        isGettingJobsForDeal: false,
      };
    case ActionType.REQUEST_GET_JOB_VALIDATION_RESULT:
      return {
        ...state,
        isGettingJobValidationResult: true,
      };
    case ActionType.GET_JOB_VALIDATION_RESULT_FAILURE:
    case ActionType.GET_JOB_VALIDATION_RESULT_SUCCESS:
      return {
        ...state,
        isGettingJobValidationResult: false,
      };
    // EDIT JOB
    case ActionType.REQUEST_EDIT_JOB:
      return {
        ...state,
        isEditingJob: true,
        editJobSuccess: undefined,
        editJobErrorMessage: undefined,
      };
    case ActionType.EDIT_JOB_SUCCESS:
      return {
        ...state,
        isEditingJob: false,
        editJobSuccess: action.payload.newJob,
        editJobErrorMessage: undefined,
      };
    case ActionType.EDIT_JOB_FAILURE:
      return {
        ...state,
        isEditingJob: false,
        editJobSuccess: undefined,
        editJobErrorMessage: action.payload.errorMessage,
      };
    case ActionType.CLEAR_EDIT_JOB:
      return {
        ...state,
        isEditingJob: false,
        editJobSuccess: undefined,
        editJobErrorMessage: undefined,
      };

    case ActionType.REQUEST_GET_JOB: {
      const fetchedEntity = state.jobs && state.jobs[action.payload.jobId];

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [action.payload.jobId]: {
            ...fetchedEntity,
            fetchAttemptedAttemptedAt: new Date(),
            fetchSucceededAt: undefined,
            fetchFailedAt: undefined,
            isFetching: true,
            fetchError: undefined,
          },
        },
      };
    }
    case ActionType.GET_JOB_SUCCESS: {
      const fetchedEntity = state.jobs && state.jobs[action.payload.jobId];

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [action.payload.jobId]: {
            ...fetchedEntity,
            entity: action.payload.job,
            fetchSucceededAt: new Date(),
            isFetching: false,
          },
        },
      };
    }
    case ActionType.GET_JOB_FAILURE: {
      const fetchedEntity = state.jobs && state.jobs[action.payload.jobId];

      return {
        ...state,
        jobs: {
          ...state.jobs,
          [action.payload.jobId]: {
            ...fetchedEntity,
            fetchFailedAtAt: new Date(),
            isFetching: false,
            fetchError: action.error,
          },
        },
      };
    }
    case ActionType.CLEAR_JOBS: {
      return {
        ...state,
        jobs: {},
      };
    }

    // UPDATE JOB
    case ActionType.REQUEST_UPDATE_JOB:
      return {
        ...state,
        isUpdatingJob: true,
        jobUpdateResults: {
          ...state.jobUpdateResults,
          [action.payload.updateJobRequestData.jobId]: {
            isUpdatedSuccessfully: undefined,
            updateError: undefined,
          },
        },
      };

    case ActionType.PARTIAL_UPDATE_JOB_REQUEST:
      return {
        ...state,
        isUpdatingJob: true,
        jobUpdateResults: {
          ...state.jobUpdateResults,
          [action.payload.jobId]: {
            isUpdatedSuccessfully: undefined,
            updateError: undefined,
          },
        },
      };
    case ActionType.UPDATE_JOB_SUCCESS:
      return {
        ...state,
        isUpdatingJob: false,
        jobUpdateResults: {
          ...state.jobUpdateResults,
          [action.payload.jobId]: {
            isUpdatedSuccessfully: true,
            updateError: undefined,
          },
        },
      };
    case ActionType.UPDATE_JOB_FAILURE:
      return {
        ...state,
        isUpdatingJob: false,
        jobUpdateResults: {
          ...state.jobUpdateResults,
          [action.payload.jobId]: {
            isUpdatedSuccessfully: false,
            updateError: action.error,
          },
        },
      };

    case ActionType.GET_JOB_ATTACHMENT_SUCCESS: {
      const fetchedEntity =
        state.jobAttachments &&
        state.jobAttachments[action.payload.jobAttachmentId];

      return {
        ...state,
        jobAttachments: {
          ...state.jobAttachments,
          [action.payload.jobAttachmentId]: {
            ...fetchedEntity,
            entity: action.payload.jobAttachment,
            fetchSucceededAt: new Date(),
            isFetching: false,
          },
        },
      };
    }
    case ActionType.GET_JOB_ATTACHMENT_FAILURE: {
      const fetchedEntity =
        state.jobAttachments && state.jobAttachments[action.payload.jobId];

      return {
        ...state,
        jobAttachments: {
          ...state.jobAttachments,
          [action.payload.jobAttachmentId]: {
            ...fetchedEntity,
            fetchFailedAtAt: new Date(),
            isFetching: false,
            fetchError: action.error,
          },
        },
      };
    }
    case ActionType.CLEAR_JOB_ATTACHMENT: {
      const jobAttachments = state.jobAttachments || {};
      delete jobAttachments[action.payload.jobAttachmentId];

      return {
        ...state,
        jobAttachments: jobAttachments,
      };
    }
    case ActionType.CLEAR_JOB_ATTACHMENTS: {
      return {
        ...state,
        jobAttachments: {},
      };
    }

    // UPDATE JOB

    case ActionType.CLEAR_JOB_UPDATE_RESULTS:
      return {
        ...state,
        jobUpdateResults: {},
      };

    // SEND INVOICE
    case ActionType.REQUEST_SEND_INVOICE_TO_CUSTOMER:
      return {
        ...state,
        isSendingInvoiceToCustomer: true,
      };
    // oxlint-disable-next-line eslint/no-duplicate-case -- FIXME: pre-existing error: Duplicate case label
    // oxlint-disable-next-line eslint/no-duplicate-case -- FIXME: pre-existing error: Duplicate case label
    // oxlint-disable-next-line eslint/no-duplicate-case -- FIXME: pre-existing error: Duplicate case label
    case ActionType.SEND_INVOICE_TO_CUSTOMER_SUCCESS:
      return {
        ...state,
        isSendingInvoiceToCustomer: false,
      };
    // oxlint-disable-next-line eslint/no-duplicate-case -- FIXME: pre-existing error: Duplicate case label
    // oxlint-disable-next-line eslint/no-duplicate-case -- FIXME: pre-existing error: Duplicate case label
    // oxlint-disable-next-line eslint/no-duplicate-case -- FIXME: pre-existing error: Duplicate case label
    case ActionType.SEND_INVOICE_TO_CUSTOMER_FAILURE:
      return {
        ...state,
        isSendingInvoiceToCustomer: false,
      };

    // ACCEPT QUOTE
    case ActionType.REQUEST_ACCEPT_QUOTE:
      return {
        ...state,
        isAccpetingQuote: true,
      };
    // eslint-disable-next-line no-duplicate-case
    case ActionType.SEND_INVOICE_TO_CUSTOMER_SUCCESS:
      return {
        ...state,
        isAccpetingQuote: false,
      };
    // eslint-disable-next-line no-duplicate-case
    case ActionType.SEND_INVOICE_TO_CUSTOMER_FAILURE:
      return {
        ...state,
        isAccpetingQuote: false,
      };

    // DECLINE JOB
    case ActionType.REQUEST_DECLINE_JOB:
      return {
        ...state,
        isDecliningJob: true,
      };
    // eslint-disable-next-line no-duplicate-case
    case ActionType.SEND_INVOICE_TO_CUSTOMER_SUCCESS:
      return {
        ...state,
        isDecliningJob: false,
      };
    // eslint-disable-next-line no-duplicate-case
    case ActionType.SEND_INVOICE_TO_CUSTOMER_FAILURE:
      return {
        ...state,
        isDecliningJob: false,
      };

    // CONVERT JOB TO INVOICE
    case ActionType.REQUEST_CONVERT_JOB_TO_INVOICE:
      return {
        ...state,
        isConvertingJobToInvoice: true,
      };
    // eslint-disable-next-line no-duplicate-case
    case ActionType.SEND_INVOICE_TO_CUSTOMER_SUCCESS:
      return {
        ...state,
        isConvertingJobToInvoice: false,
      };
    // eslint-disable-next-line no-duplicate-case
    case ActionType.SEND_INVOICE_TO_CUSTOMER_FAILURE:
      return {
        ...state,
        isConvertingJobToInvoice: false,
      };

    // DUPLICATE JOB
    case ActionType.REQUEST_DUPLICATE_JOB:
      return {
        ...state,
        isDuplicatingJob: true,
      };
    case ActionType.DUPLICATE_JOB_SUCCESS:
      return {
        ...state,
        isDuplicatingJob: false,
      };
    case ActionType.DUPLICATE_JOB_FAILURE:
      return {
        ...state,
        isDuplicatingJob: false,
      };

    // DUPLICATE JOB
    case ActionType.REQUEST_CREATE_JOB:
      return {
        ...state,
        isCreatingJob: true,
      };
    case ActionType.CREATE_JOB_SUCCESS:
      return {
        ...state,
        isCreatingJob: false,
      };
    case ActionType.CREATE_JOB_FAILURE:
      return {
        ...state,
        isCreatingJob: false,
      };

    case ActionType.GET_JOB_ATTACHMENTS_FOR_JOB_SUCCESS:
      return {
        ...state,
        jobAttachments: mergeFetchedEntities({
          cache: state.jobAttachments,
          values: action.payload,
        }),
      };

    default:
      return state;
  }
};

export default jobsReducer;
